/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  CrownIcon,
  CopyIcon,
  TwitterLogoIcon,
  WhatsappLogoIcon,
  TelegramLogoIcon,
  ClockCountdownIcon,
  ShareNetworkIcon,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * In-dashboard card that shows the signed-in streamer's VIP status:
 *  - plan / vip_until / bonus days earned
 *  - referral code + per-click share links
 *  - +3 free days per friend who claims via their link
 */
const VipStatusCard = ({ sessionId }) => {
  const [grant, setGrant] = useState(null);
  const [campaign, setCampaign] = useState(null);

  const refresh = async () => {
    try {
      const [s, c] = await Promise.all([
        api.get(`/settings/${sessionId}`),
        api.get("/promo/vip"),
      ]);
      setCampaign(c.data);
      if (s.data.plan === "pro" && s.data.vip_until) {
        // Fetch the referral code via the claim-idempotent endpoint
        try {
          const claim = await api.post("/promo/vip/claim", {
            session_id: sessionId,
            streamer_name: s.data.channel || "",
            platform: s.data.platform || "other",
            email: s.data.email || "",
          });
          setGrant(claim.data);
        } catch (e) { /* likely non-VIP; ignore */ }
      } else {
        setGrant(null);
      }
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (!grant || !grant.referral_code) return null;

  const referralUrl = `${window.location.origin}/share/vip/${grant.referral_code}`;
  const daysLeft = Math.max(
    0,
    Math.ceil((new Date(grant.vip_until).getTime() - Date.now()) / 86400000)
  );
  const bonus = grant.bonus_days || 0;
  const refs = grant.referral_count || 0;
  const views = grant.og_views || 0;
  const tier = grant.trophy_tier;

  const copy = () => {
    navigator.clipboard.writeText(referralUrl);
    toast.success("Referral link copied");
  };

  const share = (net) => {
    const u = encodeURIComponent(referralUrl);
    const text = encodeURIComponent(
      "I'm part of the LUCRÜ Top 300 VIP class — grab your free week of AI voice co-host before slots run out:"
    );
    const map = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${u}`,
      whatsapp: `https://wa.me/?text=${text}%20${u}`,
      telegram: `https://t.me/share/url?url=${u}&text=${text}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer,width=640,height=640");
  };

  return (
    <motion.div
      className="glass rounded-2xl p-5 border border-[#FF007F]/40"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="vip-status-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CrownIcon weight="fill" size={16} color="#FF007F" />
          <span className="font-display font-black text-sm uppercase tracking-widest">VIP · Pro</span>
          {tier && (
            <span
              className="text-[9px] px-1.5 py-0.5 rounded-full border font-bold uppercase tracking-widest"
              style={{
                color: tier === "gold" ? "#FFD700" : tier === "silver" ? "#C0C0C0" : "#CD7F32",
                borderColor: tier === "gold" ? "#FFD700" : tier === "silver" ? "#C0C0C0" : "#CD7F32",
              }}
              data-testid="vip-tier-badge"
            >
              {tier === "gold" ? "🥇" : tier === "silver" ? "🥈" : "🥉"} {tier}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 text-[10px] text-white/50">
          <ClockCountdownIcon size={10} />
          <span className="font-semibold text-white">{daysLeft}d</span> left
        </div>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-4">
        <div className="bg-[#05050A] border border-[#222233] rounded-lg p-2 text-center">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Views</div>
          <div className="font-display font-black text-xl text-[#FF007F]" data-testid="vip-views">{views}</div>
        </div>
        <div className="bg-[#05050A] border border-[#222233] rounded-lg p-2 text-center">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Refs</div>
          <div className="font-display font-black text-xl text-[#00E5FF]" data-testid="vip-refs">{refs}</div>
        </div>
        <div className="bg-[#05050A] border border-[#222233] rounded-lg p-2 text-center">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Bonus</div>
          <div className="font-display font-black text-xl text-[#39FF14]" data-testid="vip-bonus">+{bonus}d</div>
        </div>
        <div className="bg-[#05050A] border border-[#222233] rounded-lg p-2 text-center">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Rate</div>
          <div className="font-display font-black text-xl text-white">+{campaign?.referral_bonus_days ?? 3}d</div>
        </div>
      </div>

      <div className="text-[11px] text-white/50 mb-2 flex items-center gap-1">
        <ShareNetworkIcon size={11} /> Your referral link · +3 days per friend who claims
      </div>
      <div className="flex items-center gap-1.5">
        <button
          onClick={copy}
          className="flex-1 bg-[#05050A] border border-[#222233] rounded-lg px-3 py-2 text-[11px] font-mono text-white/70 text-left truncate hover:border-[#00E5FF]"
          data-testid="vip-copy-ref"
          title={referralUrl}
        >
          {referralUrl.replace(/^https?:\/\//, "")}
        </button>
        <button
          onClick={copy}
          className="w-8 h-8 rounded-lg border border-[#222233] flex items-center justify-center text-white/60 hover:text-[#00E5FF] hover:border-[#00E5FF]"
          data-testid="vip-copy-icon"
        >
          <CopyIcon size={12} />
        </button>
      </div>
      <div className="flex items-center gap-1.5 mt-2">
        {[
          { id: "twitter", icon: <TwitterLogoIcon size={13} /> },
          { id: "whatsapp", icon: <WhatsappLogoIcon size={13} /> },
          { id: "telegram", icon: <TelegramLogoIcon size={13} /> },
        ].map((s) => (
          <button
            key={s.id}
            onClick={() => share(s.id)}
            className="flex-1 h-8 rounded-lg border border-[#222233] flex items-center justify-center text-white/60 hover:text-white hover:border-[#00E5FF]"
            data-testid={`vip-share-${s.id}`}
          >
            {s.icon}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

export default VipStatusCard;
